/**
 * @file Formatage des grands nombres en notation idle (1.25a = 1250, b = 1e6, ... z = 1e78, aa = 1e81...) et durees.
 */
import Decimal from 'break_infinity.js';
import type { DecimalSource } from 'break_infinity.js';

/** Suffixe bijectif base 26 : 1->a ... 26->z, 27->aa, 28->ab ... (palier e = 10^(3e)). */
export const tierSuffix = (tier: number): string => {
  let n = Math.floor(tier);
  let out = '';
  while (n > 0) {
    n -= 1;
    out = String.fromCharCode(97 + (n % 26)) + out;
    n = Math.floor(n / 26);
  }
  return out;
};

/**
 * Format idle : < 1000 -> entier ; sinon mantisse a 3 chiffres significatifs + suffixe.
 * 1250 -> "1.25a" ; 999999 -> "999a" ; 1e6 -> "1.00b" ; 12.3e6 -> "12.3b".
 */
export const formatBig = (value: DecimalSource): string => {
  const d = Decimal.fromValue(value);
  if (d.lt(0)) return '-' + formatBig(d.neg());
  if (d.lt(1000)) return String(Math.floor(d.toNumber()));
  const tier = Math.floor(d.log10() / 3);
  const mant = d.div(Decimal.pow(10, tier * 3)).toNumber();
  const s = mant >= 100 ? mant.toFixed(0) : mant >= 10 ? mant.toFixed(1) : mant.toFixed(2);
  return s + tierSuffix(tier);
};

/** "1h 05m", "4m 12s", "37s". */
export const formatDuration = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
};

/** 0.0734 -> "7.3%" */
export const formatPercent = (ratio: number, digits = 1): string => `${(ratio * 100).toFixed(digits)}%`;

/** Multiplicateur : 1.08 -> "x1.08", Decimal geant -> "x1.25c". */
export const formatMult = (value: DecimalSource): string => {
  const d = Decimal.fromValue(value);
  return 'x' + (d.lt(1000) ? d.toNumber().toFixed(2) : formatBig(d));
};
